import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView, animate } from "framer-motion";
import { ArrowRight, ShieldCheck, Zap, Headphones, MessageCircle, ShoppingCart, Gauge, Users, Crosshair, Lock, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { DISCORD_URL, productImage, isCustomImage } from "@/lib/images";
import { RevealText, Reveal, SectionLabel, Marquee, StatusBadge } from "@/components/bits";
import ProductCard from "@/components/ProductCard";
import VouchCard from "@/components/VouchCard";
import LiveCordWall from "@/components/LiveCordWall";

const ASSETS = "/assets";

const MANIFESTO = [
  { n: "01", t: "EXECUTE", d: "One-click injection. No fumbling, no config hell. Launch, load, dominate before the lobby fills." },
  { n: "02", t: "INJECT", d: "Stream-proof overlays and undetected builds updated the moment a patch drops. You never go down." },
  { n: "03", t: "DOMINATE", d: "Aimbot, ESP, radar and spoofers tuned by players who win. Undetected. Undefeated. Every match." },
];

const TRUST_BADGES = [
  { icon: Zap, t: "UNDETECTED", s: "BUILT TO LAST" },
  { icon: ShieldCheck, t: "SECURE", s: "PRIVACY FOCUSED" },
  { icon: Gauge, t: "HIGH", s: "PERFORMANCE" },
  { icon: Users, t: "ACTIVE", s: "COMMUNITY" },
];

const WHY_FEATURES = [
  { icon: Crosshair, t: "ADVANCED FEATURES", d: "Powerful tools built for control." },
  { icon: RefreshCw, t: "REGULAR UPDATES", d: "Stay ahead with constant improvements." },
  { icon: Lock, t: "SECURE & PRIVATE", d: "Your safety is our priority." },
  { icon: Users, t: "ACTIVE COMMUNITY", d: "Thousands of satisfied users." },
];

const GAME_CARDS = [
  { slug: "arc", title: "ARC RAIDERS", sub: "PREMIUM SOFTWARE", img: `${ASSETS}/game-arc.png`, to: "/product/injectd-arc-raiders", live: true },
  { slug: "warzone", title: "WARZONE", sub: "COMING SOON", img: `${ASSETS}/game-warzone.png` },
  { slug: "delta", title: "DELTA FORCE", sub: "COMING SOON", img: `${ASSETS}/game-delta.png` },
  { slug: "other", title: "OTHER GAMES", sub: "AND MORE", img: `${ASSETS}/game-other.png`, to: "/store" },
];

function StatBlock({ value, label, icon: Icon, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const numeric = typeof value === "number";
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!inView || !numeric) return;
    const controls = animate(0, value, { duration: 1.6, ease: "easeOut", onUpdate: (v) => setDisplay(Math.floor(v)) });
    return () => controls.stop();
  }, [inView, value, numeric]);
  return (
    <div ref={ref} className="flex items-center gap-4 px-6 sm:px-10 py-9">
      {Icon && <Icon size={26} className="text-white/70 shrink-0" strokeWidth={1.4} />}
      <div className="text-left">
        <div className="font-anton text-3xl sm:text-4xl leading-none text-white" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
          {numeric ? display.toLocaleString() + suffix : value}
        </div>
        <div className="mt-2 font-mono text-[9px] tracking-[0.25em] text-white/40">{label}</div>
      </div>
    </div>
  );
}

function GameCard({ card }) {
  const inner = (
    <div
      data-testid={`game-card-${card.slug}`}
      className={`group relative h-60 sm:h-72 overflow-hidden rounded-2xl border transition-colors duration-500 ${card.live ? "border-white/30" : "border-white/10"}`}
    >
      <img src={card.img} alt={card.title}
        className={`absolute inset-0 h-full w-full object-cover transition-all duration-700 ${card.live ? "" : "opacity-55"} group-hover:scale-105 group-hover:opacity-90`} />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-black/5" />
      <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-5">
        <div>
          <h3 className="font-anton text-2xl sm:text-3xl uppercase leading-none text-white tracking-wide">{card.title}</h3>
          <div className={`mt-2 font-mono text-[9px] tracking-[0.25em] ${card.live ? "text-white/80" : "text-white/45"}`}>{card.sub}</div>
        </div>
        <span className={`flex h-10 w-10 items-center justify-center rounded-full border transition-all duration-300 ${card.to ? "border-white/30 text-white group-hover:bg-white group-hover:text-black" : "border-white/15 text-white/40"}`}>
          <ArrowRight size={16} />
        </span>
      </div>
    </div>
  );
  return card.to ? <Link to={card.to}>{inner}</Link> : inner;
}

export default function Home() {
  const [products, setProducts] = useState([]);
  const [vouches, setVouches] = useState([]);
  const [stats, setStats] = useState(null);
  const [live, setLive] = useState(null);
  const [maxDays, setMaxDays] = useState(0);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bannerY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/vouches?limit=6").then((r) => setVouches(r.data)).catch(() => {});
    api.get("/stats/public").then((r) => setStats(r.data)).catch(() => {});
    api.get("/discord/live").then((r) => setLive(r.data)).catch(() => {});
    api.get("/status").then((r) => {
      const days = r.data.map((p) => p.days_undetected || 0);
      setMaxDays(days.length ? Math.max(...days) : 0);
    }).catch(() => {});
  }, []);

  const flagship = products.find((p) => p.flagship);
  const featured = products.filter((p) => !p.flagship).slice(0, 6);

  return (
    <div>
      {/* HERO — art (smoke + glowing I rift + soldier) baked into hero-bg.png, like the mockup */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        <motion.div style={{ y: bannerY }} className="absolute inset-0">
          <img src={`${ASSETS}/hero-bg.png`} alt="" className="h-full w-full object-cover object-center max-lg:object-[74%_center]" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/15 to-transparent" />
          <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/80 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black to-transparent" />
        </motion.div>

        {/* STATIC PREMIUM DETAIL LAYERS — no animations, artwork only */}

        {/* 7. huge faint ghost of the ORIGINAL logo for depth (decorative layer,
               the main emblem below remains the hero) */}
        <div className="absolute inset-0 hidden lg:flex items-center justify-center pointer-events-none z-[4]">
          <img src={`${ASSETS}/logo-hero@2x.png`} alt="" data-testid="hero-emblem-ghost"
            className="opacity-[0.07]" style={{ height: "150vh", width: "auto" }} />
        </div>

        {/* 4. low-opacity technical geometry behind the smoke */}
        <svg className="absolute inset-0 h-full w-full pointer-events-none z-[4] hidden md:block"
          viewBox="0 0 1440 800" preserveAspectRatio="xMidYMid slice" data-testid="hero-geo">
          <g fill="none" stroke="#fff">
            <circle cx="720" cy="400" r="305" strokeOpacity="0.08" strokeDasharray="2 12" />
            <circle cx="720" cy="400" r="392" strokeOpacity="0.05" strokeDasharray="1 16" />
            <circle cx="720" cy="400" r="246" strokeOpacity="0.09" strokeDasharray="130 340 60 500" />
            <line x1="720" y1="26" x2="720" y2="66" strokeOpacity="0.14" />
            <line x1="720" y1="734" x2="720" y2="774" strokeOpacity="0.14" />
            <line x1="352" y1="400" x2="392" y2="400" strokeOpacity="0.12" />
            <line x1="1048" y1="400" x2="1088" y2="400" strokeOpacity="0.12" />
            <path d="M180 640 L260 560 L300 620 Z" strokeOpacity="0.08" />
            <path d="M1230 150 L1300 210 L1216 236 Z" strokeOpacity="0.08" />
            <path d="M1120 660 L1188 612 L1210 690 Z" strokeOpacity="0.06" />
            <line x1="120" y1="120" x2="420" y2="330" strokeOpacity="0.05" />
            <line x1="1330" y1="690" x2="1050" y2="480" strokeOpacity="0.05" />
            <line x1="90" y1="760" x2="90" y2="700" strokeOpacity="0.1" />
            <line x1="1350" y1="60" x2="1350" y2="120" strokeOpacity="0.1" />
            <path d="M470 120 h60 M500 90 v60" strokeOpacity="0.1" />
            <path d="M960 680 h46 M983 657 v46" strokeOpacity="0.09" />
          </g>
        </svg>

        {/* 6. thin broken framing lines — corners and edge segments, never a box */}
        <div className="absolute inset-0 pointer-events-none z-[6] hidden sm:block" data-testid="hero-frame">
          <div className="absolute left-5 top-20 h-14 w-px bg-white/20" />
          <div className="absolute left-5 top-20 h-px w-14 bg-white/20" />
          <div className="absolute right-5 top-20 h-14 w-px bg-white/20" />
          <div className="absolute right-5 top-20 h-px w-14 bg-white/20" />
          <div className="absolute right-5 bottom-6 h-14 w-px bg-white/20" />
          <div className="absolute right-5 bottom-6 h-px w-14 bg-white/20" />
          <div className="absolute left-5 top-1/3 h-20 w-px bg-white/10" />
          <div className="absolute bottom-6 left-[22%] h-px w-24 bg-white/10" />
          <div className="absolute bottom-6 left-[24%] h-2 w-px bg-white/20" />
        </div>

        {/* 5. decorative HUD typography */}
        <div className="absolute left-8 top-[92px] hidden md:block font-mono text-[9px] tracking-[0.3em] text-white/35 z-[6]" data-testid="hero-hud-id">
          GZ // 01
        </div>
        <div className="absolute left-4 top-1/2 -translate-y-1/2 -rotate-90 origin-left hidden lg:block font-mono text-[9px] tracking-[0.4em] text-white/30 z-[6]" data-testid="hero-hud-protocol">
          GZ_PROTOCOL
        </div>
        <div className="absolute left-8 bottom-28 hidden md:flex items-center gap-2 font-mono text-[9px] tracking-[0.3em] text-white/40 z-[6]" data-testid="hero-hud-status">
          <span className="h-1 w-1 bg-white/70" /> SYSTEM ONLINE
        </div>
        <div className="absolute right-8 bottom-28 hidden md:block font-mono text-[9px] tracking-[0.3em] text-white/40 z-[6]" data-testid="hero-hud-perf">
          PERFORMANCE // 100%
        </div>

        {/* CENTER EMBLEM — the actual uploaded GZ logo (transparent PNG),
            pixels untouched, scaled proportionally only. The light is emitted by
            separate layers BEHIND the image: a wide radial bloom + a tall soft
            column. No box, no blend tricks. Desktop only — on smaller screens
            the background art carries the scene. */}
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.9, duration: 1.2, ease: "easeOut" }}
          className="absolute inset-0 hidden lg:flex items-center justify-center pointer-events-none z-[5]">
          <div className="relative flex items-center justify-center h-full">
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[540px] h-[72%]"
              style={{ background: "radial-gradient(ellipse 50% 50% at 50% 50%, rgba(255,255,255,0.30) 0%, rgba(255,255,255,0.13) 30%, rgba(255,255,255,0.05) 55%, transparent 76%)" }} />
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[130px] h-[94%] bg-white/25 blur-[70px]" />
            <img src={`${ASSETS}/logo-hero@2x.png`} alt="GZ" data-testid="hero-logo-emblem"
              className="relative"
              style={{ height: "min(58vh, 520px)", width: "auto" }} />
          </div>
        </motion.div>

        {/* vertical strip — right edge, stacked words like the mockup */}
        <div className="absolute right-8 top-1/2 -translate-y-1/2 hidden lg:flex flex-col items-end gap-1 font-mono text-[10px] tracking-[0.35em] text-white/45 z-10 [text-shadow:0_1px_10px_rgba(0,0,0,0.95)]">
          <span>PLAY</span>
          <span>SMARTER</span>
          <span>GO FURTHER</span>
          <span className="mt-2 h-px w-6 bg-white/40" />
          <span className="mt-2">01 / 04</span>
        </div>

        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-5 sm:px-8 pt-28 pb-16">
          <div className="max-w-2xl">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
              className="font-mono text-[10px] tracking-[0.6em] text-white/60 [text-shadow:0_1px_10px_rgba(0,0,0,0.95)]">
              GZ
            </motion.div>

            <div className="text-flicker [filter:drop-shadow(0_2px_16px_rgba(0,0,0,0.85))]">
              <RevealText
                delay={0.5}
                className="mt-5 font-anton uppercase text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.9] tracking-wide text-smoke"
                lines={["UNLEASH", "YOUR GAME."]}
              />
            </div>

            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }}
              className="mt-7 font-mono text-[10px] sm:text-[11px] tracking-[0.3em] text-white/70 uppercase leading-loose [text-shadow:0_1px_10px_rgba(0,0,0,0.95)]"
            >
              Premium gaming software<br />built for performance.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.8 }}
              className="mt-9 flex flex-col sm:flex-row items-start gap-4"
            >
              <Link to="/store" data-testid="hero-shop-btn" className="btn-pill">
                <ShoppingCart size={15} /> GET STARTED <ArrowRight size={15} />
              </Link>
              <a href={DISCORD_URL} target="_blank" rel="noreferrer" data-testid="hero-discord-btn" className="btn-pill-ghost">
                <MessageCircle size={15} /> JOIN DISCORD <ArrowRight size={15} />
              </a>
            </motion.div>

            {/* trust badges — in flow under the buttons, like the mockup */}
            <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-x-5 gap-y-6" data-testid="hero-badges">
              {TRUST_BADGES.map((b, i) => (
                <motion.div key={b.t + b.s}
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.35 + i * 0.1 }}
                  className="flex items-center gap-3 [text-shadow:0_1px_10px_rgba(0,0,0,0.95)]"
                  data-testid={`hero-badge-${i}`}>
                  <b.icon size={20} className="text-white shrink-0 [filter:drop-shadow(0_1px_6px_rgba(0,0,0,0.9))]" strokeWidth={1.5} />
                  <div>
                    <div className="font-mono text-[9px] font-bold tracking-[0.2em] text-white">{b.t}</div>
                    <div className="font-mono text-[8px] tracking-[0.18em] text-white/45 mt-1">{b.s}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* GAME CARDS */}
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-14 sm:py-16" data-testid="game-cards">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {GAME_CARDS.map((c, i) => (
            <Reveal key={c.slug} delay={i * 0.07}><GameCard card={c} /></Reveal>
          ))}
        </div>
      </section>

      {/* PRODUCT GRID */}
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-16" data-testid="home-products">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
          <div>
            <SectionLabel>THE ARSENAL</SectionLabel>
            <h2 className="mt-4 font-anton text-4xl sm:text-6xl uppercase leading-none tracking-wide">Loadout</h2>
          </div>
          <Link to="/store" className="font-mono text-xs tracking-[0.2em] text-white/60 hover:text-white flex items-center gap-2 transition-colors">
            VIEW ALL <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {featured.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 3) * 0.08}><ProductCard p={p} index={i} /></Reveal>
          ))}
        </div>
      </section>

      {/* FLAGSHIP SPOTLIGHT */}
      {flagship && (
        <section className="relative py-28 overflow-hidden border-y border-white/10" data-testid="flagship-section">
          <div className="absolute inset-0 opacity-25">
            <img src={productImage(flagship.image)} alt="" className="h-full w-full object-cover grayscale-img" />
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40" />
          </div>
          <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 grid gap-12 lg:grid-cols-2 items-center">
            <div>
              <SectionLabel>FLAGSHIP // TOP SELLER</SectionLabel>
              <h2 className="mt-5 font-anton text-5xl sm:text-7xl uppercase leading-[0.9] tracking-wide">{flagship.name}</h2>
              <p className="mt-6 max-w-md text-white/60 leading-relaxed">{flagship.tagline}</p>
              <div className="mt-8 grid grid-cols-2 gap-3 max-w-md">
                {flagship.features.map((f) => (
                  <div key={f} className="flex items-center gap-2 text-sm text-white/70">
                    <span className="text-white">/</span> {f}
                  </div>
                ))}
              </div>
              <div className="mt-10 flex items-center gap-5">
                <Link to={`/product/${flagship.slug}`} data-testid="flagship-buy-btn" className="btn-pill">
                  GET GZ <ArrowRight size={15} />
                </Link>
                <div>
                  <div className="font-mono text-[10px] tracking-[0.2em] text-white/40">FROM</div>
                  {flagship.sale_pct > 0 && (
                    <div className="font-mono text-sm text-white/35 line-through leading-none mb-1">${flagship.from_price.toFixed(2)}</div>
                  )}
                  <div className="font-anton text-3xl text-white">${(flagship.sale_pct > 0 ? Math.min(...flagship.tiers.map((t) => t.sale_price ?? t.price)) : flagship.from_price).toFixed(2)}</div>
                </div>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="relative rounded-2xl border border-white/15 p-2 bg-black/50">
                <div className="relative overflow-hidden rounded-xl">
                  <img src={productImage(flagship.image)} alt={flagship.name} className="w-full h-[420px] object-cover" style={isCustomImage(flagship.image) ? undefined : { filter: "grayscale(100%) brightness(42%) contrast(120%)" }} />
                  {!isCustomImage(flagship.image) && (
                    <>
                      <div className="absolute inset-0 bg-black/35" />
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <svg viewBox="0 0 24 40" className="h-24" style={{ filter: "drop-shadow(0 0 30px rgba(255,255,255,0.35))" }}>
                          <path d="M12 0 L15.5 5 L13.4 7.2 L13.4 32.8 L15.5 35 L12 40 L8.5 35 L10.6 32.8 L10.6 7.2 L8.5 5 Z" fill="#fff" />
                        </svg>
                        <div className="mt-4 font-anton text-2xl uppercase tracking-[0.18em] text-white">{flagship.game}</div>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4"><StatusBadge status="undetected" /></div>
            </div>
          </div>
        </section>
      )}

      {/* WHY CHOOSE GZ */}
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24 sm:py-28" data-testid="why-choose">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4 flex flex-col justify-center">
            <div className="font-mono text-[10px] tracking-[0.5em] text-white/45">WHY CHOOSE</div>
            <h2 className="wordmark-stripe mt-4 text-7xl sm:text-8xl uppercase leading-[0.9]" data-testid="why-wordmark">GZ</h2>
            <p className="mt-7 max-w-sm font-mono text-[10px] sm:text-[11px] tracking-[0.15em] text-white/55 leading-relaxed uppercase">
              We focus on quality, security, and performance to give you the best gaming experience.
              Built by players, for players.
            </p>
            <Link to="/faq" data-testid="why-features-btn" className="btn-pill-ghost mt-9 self-start">
              SEE ALL FEATURES <ArrowRight size={14} />
            </Link>
          </div>
          <div className="lg:col-span-5 grid gap-4 sm:grid-cols-2">
            {WHY_FEATURES.map((f, i) => (
              <Reveal key={f.t} delay={i * 0.07}>
                <div className="h-full rounded-2xl border border-white/10 bg-[#0d0f14] p-7 hover:border-white/25 transition-colors" data-testid={`feature-card-${i}`}>
                  <f.icon size={24} className="text-white" strokeWidth={1.5} />
                  <h3 className="mt-5 font-anton text-lg sm:text-xl uppercase tracking-wider text-white leading-tight">{f.t}</h3>
                  <p className="mt-3 font-mono text-[9px] tracking-[0.12em] text-white/45 leading-relaxed uppercase">{f.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="lg:col-span-3">
            <Reveal delay={0.15}>
              <div className="relative h-full min-h-[320px] rounded-2xl border border-white/10 overflow-hidden flex flex-col justify-end p-7" data-testid="community-cta">
                <img src={`${ASSETS}/community-bg.png`} alt="" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/20" />
                <div className="relative">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/30 bg-black/40 backdrop-blur mb-5">
                    <MessageCircle size={18} className="text-white" />
                  </span>
                  <div className="font-mono text-[9px] tracking-[0.3em] text-white/60">JOIN OUR</div>
                  <h3 className="font-anton text-3xl uppercase tracking-wide text-white mt-1">Community</h3>
                  <p className="mt-3 font-mono text-[9px] tracking-[0.12em] text-white/55 uppercase leading-relaxed">
                    Be part of a growing community. Get support, updates, and more.
                  </p>
                  <a href={DISCORD_URL} target="_blank" rel="noreferrer" data-testid="cta-discord-btn" className="btn-pill-ghost mt-6 !py-3 !px-5 text-[10px]">
                    DISCORD.GG/INJECTD <ArrowRight size={13} />
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* VOUCHES WALL */}
      {vouches.length > 0 && (
        <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24" data-testid="home-vouches">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
            <div>
              <SectionLabel>VERIFIED BY THE COMMUNITY</SectionLabel>
              <h2 className="mt-4 font-anton text-4xl sm:text-6xl uppercase leading-none tracking-wide">Vouches</h2>
            </div>
            <Link to="/vouches" className="font-mono text-xs tracking-[0.2em] text-white/60 hover:text-white flex items-center gap-2 transition-colors">
              READ ALL <ArrowRight size={14} />
            </Link>
          </div>
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-6">
            {vouches.map((v, i) => (
              <Reveal key={v.message_id} delay={(i % 3) * 0.06}><VouchCard v={v} /></Reveal>
            ))}
          </div>
        </section>
      )}

      {/* MANIFESTO */}
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24" data-testid="manifesto-section">
        <SectionLabel>THE DOCTRINE</SectionLabel>
        <h2 className="mt-4 font-anton text-4xl sm:text-6xl uppercase leading-none tracking-wide mb-16">How we win</h2>
        <div className="space-y-px rounded-2xl overflow-hidden bg-white/10 border border-white/10">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.n} delay={i * 0.05}>
              <div className="bg-black grid md:grid-cols-[120px_1fr] gap-6 p-8 sm:p-12 group hover:bg-white/[0.02] transition-colors">
                <div className="font-anton text-6xl sm:text-7xl text-white/15 group-hover:text-white/40 transition-colors leading-none">{m.n}</div>
                <div>
                  <h3 className="font-anton text-3xl sm:text-4xl uppercase tracking-wide">{m.t}</h3>
                  <p className="mt-3 max-w-2xl text-white/50 leading-relaxed">{m.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* LIVE DISCORD WALL */}
      <LiveCordWall />

      {/* STATS STRIP — live data */}
      <section className="border-t border-white/10" data-testid="stats-band">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10">
          <StatBlock value={live?.member_count || 0} label="COMMUNITY MEMBERS" icon={Users} suffix="+" />
          <StatBlock value={maxDays} label="DAYS UNDETECTED" icon={ShieldCheck} suffix="+" />
          <StatBlock value="24/7" label="LIVE SUPPORT" icon={Headphones} />
          <StatBlock value={stats?.vouches || 0} label="VERIFIED VOUCHES" icon={Zap} suffix="+" />
        </div>
      </section>
    </div>
  );
}
